import { Box, Tooltip, Typography } from "@mui/material";

import { CALIBRATE_FONT_FAMILY_DISPLAY } from "components/hoc/Theme";

import { avatarTooltip, stackParts, type AvatarPerson } from "./avatarModel";
import UserAvatar from "./UserAvatar";

interface AvatarStackProps {
  people: AvatarPerson[];
  /** How many avatars to draw before the "+N" chip. */
  max?: number;
  size?: number;
  /** Shown when there is nobody. */
  empty?: string;
}

/** Overlapping avatars; the rest behind a "+N" chip whose tooltip names them. */
export default function AvatarStack({
  people,
  max = 2,
  size = 24,
  empty = "-",
}: AvatarStackProps) {
  if (people.length === 0) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  const { shown, hidden } = stackParts(people, max);
  const overlap = `-${Math.round(size / 4)}px`;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      {shown.map((person, index) => (
        <Box
          key={person.id}
          component="span"
          sx={{ display: "inline-flex", ml: index ? overlap : 0 }}
        >
          <UserAvatar person={person} size={size} />
        </Box>
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map(avatarTooltip).join(", ")} arrow>
          <Box
            component="span"
            aria-label={`${hidden.length} more`}
            sx={{
              ml: overlap,
              width: size,
              height: size,
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              border: "1.5px solid #FFFFFF",
              bgcolor: "#C9CED3",
              color: "#3C4046",
              fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
              fontSize: Math.max(9, Math.round(size * 0.36)),
              fontWeight: 700,
            }}
          >
            +{hidden.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
